import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { ROUTES } from "~/router";
import { AuthLayout } from "~/screens/Auth/Layout/AuthLayout";
import {
  INTERSTITIAL_VARIANTS,
  PATIENT_STATUS,
  patientScreeningSchema,
  ScreeningFormSchema,
} from "~/services";
import { isInterstitialVariant } from "~/services/booking-flows/utils";
import { useUsers } from "~/shared";
import { useAuthStore, useBookingFlowStore, usePatientStore } from "~/stores";

export const QualifierQuestions = () => {
  const patientType = usePatientStore((state) => state.patientType);
  const isRescheduling = usePatientStore((state) => state.isRescheduling);
  const setIsRescheduling = usePatientStore((state) => state.setIsRescheduling);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());
  const interstitialVariant = useBookingFlowStore(
    (state) => state.interstitialVariant,
  );
  const { getCurrentUser } = useUsers();
  const { data: currentUser } = getCurrentUser(isLoggedIn);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const form = useForm<ScreeningFormSchema>({
    resolver: zodResolver(patientScreeningSchema),
    defaultValues: {
      patientType: patientType,
    },
  });

  const isTerminalScreen =
    pathname === ROUTES.getStartedJoinedWaitlist ||
    pathname === ROUTES.getStartedNonValidInsurance;

  const isStateInsuranceScreen = pathname === ROUTES.getStartedInsurance;

  const isFirstFlowScreen =
    pathname === ROUTES.getStarted ||
    pathname === ROUTES.getStartedGoal ||
    (pathname === ROUTES.getStartedOrientingInfo &&
      interstitialVariant === INTERSTITIAL_VARIANTS.ONLY_CONTEXT) ||
    (isStateInsuranceScreen &&
      !isLoggedIn &&
      !isInterstitialVariant(interstitialVariant));

  const isEntryOrTerminalScreen = isTerminalScreen || isFirstFlowScreen;

  const canGoBack = !isEntryOrTerminalScreen;

  const backTo =
    isStateInsuranceScreen && isLoggedIn
      ? () => {
          if (patientType === PATIENT_STATUS.COACH) {
            navigate(ROUTES.hubMeetCoach);
            return;
          }
          if (isRescheduling) {
            setIsRescheduling(false);
            navigate(ROUTES.hubHome);
            return;
          }
          if (!currentUser) return;
          navigate(
            currentUser.ehrId ? ROUTES.hubMeetClinician : ROUTES.hubBookVisit,
          );
        }
      : undefined;

  if (isLoggedIn && patientType === null) {
    return <Navigate to={ROUTES.hubHome} replace />;
  }

  return (
    <AuthLayout
      canGoBack={canGoBack}
      backTo={backTo}
      bodyClassName="max-w-lg mx-auto md:pt-0"
      buttonClassName="w-fit"
    >
      <FormProvider {...form}>
        <Outlet />
      </FormProvider>
    </AuthLayout>
  );
};
